import { Field, FieldInputProps, Form, Formik, FormikProps } from 'formik';
import { UiKit } from '@datavant/uikit';
import * as Yup from 'yup';
import YupPassword from 'yup-password';
import React from 'react';
import { useToast } from '@chakra-ui/react';
import { trpc } from '../../../utils/trpc';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
YupPassword(Yup);

export const doesPasswordContainPartOfUsername = ({
    password,
    username,
}: {
    password?: string;
    username: string;
}) => {
    const parts = username.split(new RegExp(/[.,\-_#@]/, 'ig'));
    return parts.some(
        part => part.length > 3 && password && password.toLowerCase().includes(part.toLowerCase())
    );
};

type FormProps = {
    email: string;
    validateEmail: string;
    firstName: string;
    lastName: string;
    password: string;
    validatePassword: string;
};

const FormSchema = Yup.object().shape({
    validateEmail: Yup.string().oneOf([Yup.ref('email')], 'Email must match'),
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    password: Yup.string()
        .password()
        .required('Password is required')
        .minLowercase(1, 'Password must contain at least one lowercase letter')
        .minUppercase(1, 'Password must contain at least one uppercase letter')
        .minNumbers(1, 'Password must contain at least one number')
        .minSymbols(1, 'Password must contain at least one symbol (e.g., !@#$%^&*)')
        .test(
            'firstNamePassword',
            'Password cannot contain first name',
            (value, context) => !value?.toLowerCase().includes(context.parent.firstName.toLowerCase())
        )
        .test(
            'lastNamePassword',
            'Password cannot contain last name',
            (value, context) => !value?.toLowerCase().includes(context.parent.lastName.toLowerCase())
        )
        .test(
            'usernamePassword',
            'Password cannot contain any parts of username',
            (value, context) =>
                !doesPasswordContainPartOfUsername({ password: value, username: context.parent.email })
        ),
    validatePassword: Yup.string().oneOf([Yup.ref('password')], 'Password must match'),
});

export const Register = ({
    email,
    onBackToSignInClick,
    onRegisterSuccess,
}: {
    email: string;
    onBackToSignInClick?: () => void;
    onRegisterSuccess?: () => void;
}) => {
    const toast = useToast();
    const { error, mutate, isLoading, isSuccess } = trpc.tenant.createNewUser.useMutation();

    if (error) {
        datadogLogs.logger.error('Error registering new user using embedded auth flow', {
            error,
        });
        datadogRum.addError(error);
        toast({
            title: 'There was an error with your registration. Please try again or reach out to customer service via help options below.',
            status: 'error',
            duration: 2000,
            isClosable: true,
            position: 'top-right',
        });
    }

    return (
        <Formik<FormProps>
            initialValues={{
                email,
                validateEmail: '',
                password: '',
                validatePassword: '',
                firstName: '',
                lastName: '',
            }}
            onSubmit={async ({ firstName, lastName, email, password }) => {
                mutate(
                    {
                        firstName,
                        lastName,
                        email,
                        password,
                    },
                    {
                        onSuccess: result => {
                            datadogRum.addAction('registered user', result);
                            onRegisterSuccess?.();
                        },
                    }
                );
            }}
            validationSchema={FormSchema}
        >
            {({
                errors,
                values: { validateEmail, firstName, lastName, password, validatePassword },
                isValid,
            }) => (
                <Form>
                    <UiKit.Chakra.VStack gap={4} width={400} px={42} pb={42}>
                        <UiKit.Chakra.Text fontSize={16} fontWeight={500} lineHeight={'24px'} mb={4}>
                            Create New Account
                        </UiKit.Chakra.Text>

                        <Field name={'email'}>
                            {({
                                field,
                            }: {
                                field: FieldInputProps<FormProps['email']>;
                                form: FormikProps<FormProps>;
                            }) => (
                                <UiKit.Chakra.FormControl>
                                    <UiKit.Chakra.FormLabel style={{ color: '#4f4e4c', fontWeight: 500 }}>
                                        Work Email
                                    </UiKit.Chakra.FormLabel>
                                    <UiKit.Chakra.Text fontSize={12} pb={1} mt={-2} color={'#4f4E4c'}>
                                        Enter your organization email to verify your identity and establish
                                        your credentials.
                                    </UiKit.Chakra.Text>
                                    <UiKit.Chakra.Input
                                        {...field}
                                        isDisabled
                                        type="email"
                                        fontSize={14}
                                    ></UiKit.Chakra.Input>
                                </UiKit.Chakra.FormControl>
                            )}
                        </Field>

                        <Field name={'validateEmail'}>
                            {({
                                field,
                                form,
                            }: {
                                field: FieldInputProps<FormProps['validateEmail']>;
                                form: FormikProps<FormProps>;
                            }) => (
                                <UiKit.Chakra.FormControl
                                    isRequired
                                    isInvalid={!!form.errors.validateEmail && !!form.touched.validateEmail}
                                    isDisabled={isLoading || isSuccess}
                                >
                                    <UiKit.Chakra.FormLabel style={{ color: '#4f4e4c', fontWeight: 500 }}>
                                        Validate Work Email
                                    </UiKit.Chakra.FormLabel>
                                    <UiKit.Chakra.Input
                                        {...field}
                                        type="email"
                                        fontSize={14}
                                    ></UiKit.Chakra.Input>
                                    <UiKit.Chakra.FormErrorMessage>
                                        {form.errors.validateEmail}
                                    </UiKit.Chakra.FormErrorMessage>
                                </UiKit.Chakra.FormControl>
                            )}
                        </Field>
                        <UiKit.Chakra.Flex gap={4} mt={4}>
                            <Field name={'firstName'}>
                                {({
                                    field,
                                    form,
                                }: {
                                    field: FieldInputProps<FormProps['firstName']>;
                                    form: FormikProps<FormProps>;
                                }) => (
                                    <UiKit.Chakra.FormControl
                                        isRequired
                                        isInvalid={!!form.errors.firstName && !!form.touched.firstName}
                                        isDisabled={
                                            isLoading || isSuccess || !validateEmail || !!errors.validateEmail
                                        }
                                        flex="1"
                                    >
                                        <UiKit.Chakra.FormLabel style={{ color: '#4f4e4c', fontWeight: 500 }}>
                                            First Name
                                        </UiKit.Chakra.FormLabel>
                                        <UiKit.Chakra.Input
                                            {...field}
                                            type="text"
                                            fontSize={14}
                                        ></UiKit.Chakra.Input>
                                        <UiKit.Chakra.FormErrorMessage>
                                            {form.errors.firstName}
                                        </UiKit.Chakra.FormErrorMessage>
                                    </UiKit.Chakra.FormControl>
                                )}
                            </Field>

                            <Field name={'lastName'}>
                                {({
                                    field,
                                    form,
                                }: {
                                    field: FieldInputProps<FormProps['lastName']>;
                                    form: FormikProps<FormProps>;
                                }) => (
                                    <UiKit.Chakra.FormControl
                                        isRequired
                                        isInvalid={!!form.errors.lastName && !!form.touched.lastName}
                                        isDisabled={
                                            isLoading || isSuccess || !validateEmail || !!errors.validateEmail
                                        }
                                        flex="1"
                                    >
                                        <UiKit.Chakra.FormLabel style={{ color: '#4f4e4c', fontWeight: 500 }}>
                                            Last Name
                                        </UiKit.Chakra.FormLabel>
                                        <UiKit.Chakra.Input
                                            {...field}
                                            type="text"
                                            fontSize={14}
                                        ></UiKit.Chakra.Input>
                                        <UiKit.Chakra.FormErrorMessage>
                                            {form.errors.lastName}
                                        </UiKit.Chakra.FormErrorMessage>
                                    </UiKit.Chakra.FormControl>
                                )}
                            </Field>
                        </UiKit.Chakra.Flex>

                        <Field name={'password'}>
                            {({
                                field,
                                form,
                            }: {
                                field: FieldInputProps<FormProps['password']>;
                                form: FormikProps<FormProps>;
                            }) => (
                                <UiKit.Chakra.FormControl
                                    isRequired
                                    isInvalid={!!form.errors.password && !!form.touched.password}
                                    isDisabled={
                                        isSuccess ||
                                        isLoading ||
                                        !validateEmail ||
                                        !!errors.validateEmail ||
                                        !firstName ||
                                        !!errors.firstName ||
                                        !lastName ||
                                        !!errors.lastName
                                    }
                                >
                                    <UiKit.Chakra.FormLabel style={{ color: '#4f4e4c', fontWeight: 500 }}>
                                        Create Password
                                    </UiKit.Chakra.FormLabel>
                                    <UiKit.Chakra.Text fontSize={12} pb={1} mt={-2} color={'#4f4E4c'}>
                                        Create a unique password that you will use to login. Do not use the
                                        password on the Provider Packet/Pull List.
                                    </UiKit.Chakra.Text>
                                    <UiKit.Chakra.Input
                                        {...field}
                                        onChange={value => {
                                            void form.setFieldTouched('password');
                                            field.onChange(value);
                                        }}
                                        type="password"
                                        fontSize={14}
                                    ></UiKit.Chakra.Input>
                                    <UiKit.Chakra.Text fontSize={12} mt={2} color={'#4f4E4c'}>
                                        Password must:
                                    </UiKit.Chakra.Text>
                                    <UiKit.Chakra.List spacing={0.5} pl={2} fontSize={12} color={'#4f4E4c'}>
                                        <UiKit.Chakra.ListItem>
                                            • Have at least 8 characters
                                        </UiKit.Chakra.ListItem>
                                        <UiKit.Chakra.ListItem>
                                            • Have at least one uppercase and one lowercase letter
                                        </UiKit.Chakra.ListItem>
                                        <UiKit.Chakra.ListItem>
                                            • Have at least one number and one symbol
                                        </UiKit.Chakra.ListItem>
                                        <UiKit.Chakra.ListItem>
                                            • Not contain your first or last name
                                        </UiKit.Chakra.ListItem>
                                    </UiKit.Chakra.List>
                                    <UiKit.Chakra.FormErrorMessage>
                                        {form.errors.password}
                                    </UiKit.Chakra.FormErrorMessage>
                                </UiKit.Chakra.FormControl>
                            )}
                        </Field>

                        <Field name={'validatePassword'}>
                            {({
                                field,
                                form,
                            }: {
                                field: FieldInputProps<FormProps['validatePassword']>;
                                form: FormikProps<FormProps>;
                            }) => (
                                <UiKit.Chakra.FormControl
                                    isRequired
                                    isInvalid={
                                        !!form.errors.validatePassword && !!form.touched.validatePassword
                                    }
                                    isDisabled={
                                        isSuccess ||
                                        isLoading ||
                                        !validateEmail ||
                                        !!errors.validateEmail ||
                                        !firstName ||
                                        !!errors.firstName ||
                                        !lastName ||
                                        !!errors.lastName ||
                                        !password ||
                                        !!errors.password
                                    }
                                >
                                    <UiKit.Chakra.FormLabel style={{ color: '#4f4e4c', fontWeight: 500 }}>
                                        Confirm Password
                                    </UiKit.Chakra.FormLabel>
                                    <UiKit.Chakra.Input
                                        {...field}
                                        type="password"
                                        fontSize={14}
                                    ></UiKit.Chakra.Input>
                                    <UiKit.Chakra.FormErrorMessage>
                                        {form.errors.validatePassword}
                                    </UiKit.Chakra.FormErrorMessage>
                                </UiKit.Chakra.FormControl>
                            )}
                        </Field>

                        <UiKit.Chakra.Button
                            type="submit"
                            isLoading={isLoading}
                            backgroundColor={'#006ccf'}
                            color={'white'}
                            borderRadius={24}
                            height={50}
                            fontFamily={'Inter, montserrat-okta, Arial, Helvetica, sans-serif'}
                            isDisabled={
                                isSuccess ||
                                isLoading ||
                                !isValid ||
                                !validateEmail ||
                                !firstName ||
                                !lastName ||
                                !password ||
                                !validatePassword
                            }
                        >
                            Create Account
                        </UiKit.Chakra.Button>
                        <UiKit.Chakra.Button
                            type="reset"
                            color={'#006ccf'}
                            variant={'link'}
                            onClick={() => onBackToSignInClick?.()}
                            mt={-2}
                            fontFamily={'Lato, sans-serif'}
                            isDisabled={isLoading}
                        >
                            Back to sign in
                        </UiKit.Chakra.Button>
                    </UiKit.Chakra.VStack>
                </Form>
            )}
        </Formik>
    );
};
